import React, { useEffect, useMemo, useState } from 'react'
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts'
import { LoadingSpinner, Text } from '../../components/ui'
import { marketingNotasService } from '../../services/marketingNotas'
import type { EstoqueCategoria, EstoqueItem, EstoqueSaldo } from '../../types/estoque'
import type { MarketingInvoice } from '../../types/marketingNotas'
import { formatCurrency, formatDateOnlyPtBr } from '../Marketing/notasUi'
import {
  formatQuantity,
  itemTypeLabel,
  statusBadgeClass,
  tabButtonClass,
} from './estoqueUi'

type DashTab = 'produtos' | 'compras'

type Props = {
  itens: EstoqueItem[]
  saldos: EstoqueSaldo[]
  categorias: EstoqueCategoria[]
  loading: boolean
}

type PurchaseRow = {
  key: string
  issueDate: string
  invoiceNumber: string
  invoiceStatus: string
  itemName: string
  itemCode: number | null
  itemUnit: string
  supplierName: string
  quantity: number
  unitValue: number
  totalValue: number
}

const EmptyChart: React.FC = () => (
  <div className="flex h-full items-center justify-center text-sm text-gray-400">Sem dados</div>
)

function chartHeight(count: number): number {
  return Math.max(220, count * 36)
}

function truncateLabel(s: string, maxLen: number): string {
  if (s.length <= maxLen) return s
  return `${s.slice(0, maxLen - 1)}…`
}

const ProdutosPanel: React.FC<Props> = ({ itens, saldos, categorias, loading }) => {
  const saldoByItem = useMemo(() => {
    const map = new Map<string, number>()
    for (const saldo of saldos) {
      if (saldo.deleted_at) continue
      map.set(saldo.item_id, (map.get(saldo.item_id) || 0) + Number(saldo.quantity || 0))
    }
    return map
  }, [saldos])

  const categoryNameById = useMemo(() => {
    const map = new Map<string, string>()
    for (const c of categorias) map.set(c.id, c.name)
    return map
  }, [categorias])

  const rows = useMemo(() => {
    return itens
      .filter((item) => !item.deleted_at)
      .map((item) => {
        const quantity = saldoByItem.get(item.id) || 0
        const minStock = Number(item.min_stock || 0)
        return {
          id: item.id,
          code: item.code,
          name: item.name,
          itemType: item.item_type,
          categoryName:
            item.category?.name || categoryNameById.get(item.category_id) || '—',
          quantity,
          minStock,
          belowMinimum: quantity < minStock,
          isActive: item.is_active,
          availableForRequest: Boolean(item.available_for_request),
        }
      })
      .sort((a, b) => {
        if (a.belowMinimum !== b.belowMinimum) return a.belowMinimum ? -1 : 1
        return a.name.localeCompare(b.name, 'pt-BR')
      })
  }, [itens, saldoByItem, categoryNameById])

  const totals = useMemo(() => {
    let active = 0
    let inactive = 0
    let belowMin = 0
    let available = 0
    let quantity = 0
    for (const row of rows) {
      if (row.isActive) active += 1
      else inactive += 1
      if (row.belowMinimum) belowMin += 1
      if (row.availableForRequest) available += 1
      quantity += row.quantity
    }
    return { active, inactive, belowMin, available, quantity, distinct: rows.length }
  }, [rows])

  const byTypeChart = useMemo(() => {
    const map = new Map<string, number>()
    for (const row of rows) {
      const label = itemTypeLabel(row.itemType)
      map.set(label, (map.get(label) || 0) + 1)
    }
    return Array.from(map.entries()).map(([name, count]) => ({ name, count }))
  }, [rows])

  const topSaldoChart = useMemo(
    () =>
      [...rows]
        .sort((a, b) => b.quantity - a.quantity)
        .slice(0, 10)
        .map((row) => ({
          name: truncateLabel(row.name, 28),
          count: row.quantity,
        })),
    [rows]
  )

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-sm text-gray-500 dark:text-gray-400">
        <LoadingSpinner />
        <span className="ml-2">Carregando produtos...</span>
      </div>
    )
  }

  return (
    <>
      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-3 xl:grid-cols-6">
        <div className="rounded-lg bg-white p-5 shadow dark:bg-gray-800">
          <div className="text-xs text-gray-500 dark:text-gray-400">Itens</div>
          <div className="mt-1 text-3xl font-semibold text-gray-900 dark:text-white">
            {totals.distinct}
          </div>
        </div>
        <div className="rounded-lg bg-white p-5 shadow dark:bg-gray-800">
          <div className="text-xs text-gray-500 dark:text-gray-400">Ativos</div>
          <div className="mt-1 text-3xl font-semibold text-primary-600 dark:text-primary-400">
            {totals.active}
          </div>
        </div>
        <div className="rounded-lg bg-white p-5 shadow dark:bg-gray-800">
          <div className="text-xs text-gray-500 dark:text-gray-400">Inativos</div>
          <div className="mt-1 text-3xl font-semibold text-gray-500 dark:text-gray-300">
            {totals.inactive}
          </div>
        </div>
        <div className="rounded-lg bg-white p-5 shadow dark:bg-gray-800">
          <div className="text-xs text-gray-500 dark:text-gray-400">P/ solicitação</div>
          <div className="mt-1 text-3xl font-semibold text-sky-600 dark:text-sky-400">
            {totals.available}
          </div>
        </div>
        <div className="rounded-lg bg-white p-5 shadow dark:bg-gray-800">
          <div className="text-xs text-gray-500 dark:text-gray-400">Abaixo do mínimo</div>
          <div className="mt-1 text-3xl font-semibold text-orange-600 dark:text-orange-400">
            {totals.belowMin}
          </div>
        </div>
        <div className="rounded-lg bg-white p-5 shadow dark:bg-gray-800">
          <div className="text-xs text-gray-500 dark:text-gray-400">Qtd. em estoque</div>
          <div className="mt-1 text-3xl font-semibold text-green-600 dark:text-green-400">
            {formatQuantity(totals.quantity)}
          </div>
        </div>
      </div>

      <div className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-lg bg-white p-5 shadow dark:bg-gray-800">
          <Text size="sm" className="mb-1 font-medium text-gray-700 dark:text-gray-300">
            Itens por tipo
          </Text>
          <Text size="xs" color="muted" className="mb-3">
            Distribuição brinde × material
          </Text>
          <div style={{ height: chartHeight(byTypeChart.length || 2) }}>
            {byTypeChart.length === 0 ? (
              <EmptyChart />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart layout="vertical" data={byTypeChart} margin={{ left: 16, right: 16 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
                  <YAxis type="category" dataKey="name" width={90} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="count" name="Itens" fill="#008B95" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
        <div className="rounded-lg bg-white p-5 shadow dark:bg-gray-800">
          <Text size="sm" className="mb-1 font-medium text-gray-700 dark:text-gray-300">
            Maior saldo (top 10)
          </Text>
          <Text size="xs" color="muted" className="mb-3">
            Quantidade total somando todos os locais
          </Text>
          <div style={{ height: chartHeight(topSaldoChart.length || 2) }}>
            {topSaldoChart.length === 0 ? (
              <EmptyChart />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart layout="vertical" data={topSaldoChart} margin={{ left: 8, right: 16 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
                  <YAxis type="category" dataKey="name" width={160} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="count" name="Quantidade" fill="#22c55e" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      <div className="rounded-lg bg-white shadow dark:bg-gray-800">
        <div className="border-b border-gray-200 px-5 py-4 dark:border-gray-700">
          <Text size="sm" className="font-medium text-gray-700 dark:text-gray-300">
            Relatório de produtos
          </Text>
          <Text size="xs" color="muted" className="mt-0.5">
            Saldo consolidado por item. Itens abaixo do mínimo aparecem primeiro.
          </Text>
        </div>
        {rows.length === 0 ? (
          <div className="px-5 py-10 text-center text-sm text-gray-400">Nenhum item cadastrado.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900/40">
                <tr>
                  <th className="px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Código
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Item
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Tipo
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Categoria
                  </th>
                  <th className="px-5 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Saldo
                  </th>
                  <th className="px-5 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Mínimo
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Status
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Solicitação
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {rows.map((row) => (
                  <tr key={row.id} className="bg-white dark:bg-gray-800">
                    <td className="px-5 py-3 text-sm tabular-nums text-gray-600 dark:text-gray-300">
                      {row.code}
                    </td>
                    <td className="px-5 py-3 text-sm font-medium text-gray-900 dark:text-white">
                      {row.name}
                    </td>
                    <td className="px-5 py-3 text-sm text-gray-600 dark:text-gray-300">
                      {itemTypeLabel(row.itemType)}
                    </td>
                    <td className="px-5 py-3 text-sm text-gray-600 dark:text-gray-300">
                      {row.categoryName}
                    </td>
                    <td
                      className={`px-5 py-3 text-right text-sm tabular-nums ${
                        row.belowMinimum
                          ? 'font-semibold text-orange-600 dark:text-orange-400'
                          : 'text-gray-900 dark:text-white'
                      }`}
                    >
                      {formatQuantity(row.quantity)}
                    </td>
                    <td className="px-5 py-3 text-right text-sm tabular-nums text-gray-600 dark:text-gray-300">
                      {formatQuantity(row.minStock)}
                    </td>
                    <td className="px-5 py-3">
                      <span
                        className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${statusBadgeClass(row.isActive)}`}
                      >
                        {row.isActive ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-sm text-gray-600 dark:text-gray-300">
                      {row.availableForRequest ? 'Disponível' : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  )
}

const ComprasPanel: React.FC = () => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [invoices, setInvoices] = useState<MarketingInvoice[]>([])

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setLoading(true)
      setError(null)
      try {
        const data = await marketingNotasService.listInvoices({ documentType: 'nfe' })
        if (!cancelled) setInvoices(data)
      } catch (err) {
        if (!cancelled) {
          setInvoices([])
          setError(err instanceof Error ? err.message : 'Erro ao carregar histórico de compras.')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [])

  const purchaseRows = useMemo(() => {
    const rows: PurchaseRow[] = []
    for (const invoice of invoices) {
      for (const item of invoice.items || []) {
        if (item.deleted_at || !item.stock_item_id) continue
        rows.push({
          key: item.id,
          issueDate: invoice.issue_date,
          invoiceNumber: invoice.number,
          invoiceStatus: invoice.status,
          itemName: item.stock_item?.name || item.description || 'Item',
          itemCode: item.stock_item?.code ?? null,
          itemUnit: item.stock_item?.unit || 'un',
          supplierName: invoice.supplier?.name || '—',
          quantity: Number(item.quantity || 0),
          unitValue: Number(item.unit_value || 0),
          totalValue: Number(item.total_value || 0),
        })
      }
    }
    return rows.sort((a, b) => {
      if (a.issueDate !== b.issueDate) return b.issueDate.localeCompare(a.issueDate)
      return a.itemName.localeCompare(b.itemName, 'pt-BR')
    })
  }, [invoices])

  /** Cards e gráficos usam só NF confirmadas; a tabela mantém rascunhos. */
  const confirmedPurchaseRows = useMemo(
    () => purchaseRows.filter((row) => row.invoiceStatus === 'confirmed'),
    [purchaseRows]
  )

  const totals = useMemo(() => {
    let quantity = 0
    let amount = 0
    const suppliers = new Set<string>()
    const items = new Set<string>()
    for (const row of confirmedPurchaseRows) {
      quantity += row.quantity
      amount += row.totalValue
      if (row.supplierName !== '—') suppliers.add(row.supplierName)
      items.add(row.itemName)
    }
    return {
      lines: confirmedPurchaseRows.length,
      quantity,
      amount,
      suppliers: suppliers.size,
      items: items.size,
    }
  }, [confirmedPurchaseRows])

  const topSuppliersChart = useMemo(() => {
    const map = new Map<string, number>()
    for (const row of confirmedPurchaseRows) {
      map.set(row.supplierName, (map.get(row.supplierName) || 0) + row.totalValue)
    }
    return Array.from(map.entries())
      .map(([name, count]) => ({ name: truncateLabel(name, 28), count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10)
  }, [confirmedPurchaseRows])

  const topItemsChart = useMemo(() => {
    const map = new Map<string, number>()
    for (const row of confirmedPurchaseRows) {
      map.set(row.itemName, (map.get(row.itemName) || 0) + row.totalValue)
    }
    return Array.from(map.entries())
      .map(([name, count]) => ({ name: truncateLabel(name, 28), count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10)
  }, [confirmedPurchaseRows])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-sm text-gray-500 dark:text-gray-400">
        <LoadingSpinner />
        <span className="ml-2">Carregando compras...</span>
      </div>
    )
  }

  return (
    <>
      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
          {error}
        </div>
      )}

      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-5">
        <div className="rounded-lg bg-white p-5 shadow dark:bg-gray-800">
          <div className="text-xs text-gray-500 dark:text-gray-400">Linhas de compra</div>
          <div className="mt-1 text-3xl font-semibold text-gray-900 dark:text-white">
            {totals.lines}
          </div>
        </div>
        <div className="rounded-lg bg-white p-5 shadow dark:bg-gray-800">
          <div className="text-xs text-gray-500 dark:text-gray-400">Itens distintos</div>
          <div className="mt-1 text-3xl font-semibold text-primary-600 dark:text-primary-400">
            {totals.items}
          </div>
        </div>
        <div className="rounded-lg bg-white p-5 shadow dark:bg-gray-800">
          <div className="text-xs text-gray-500 dark:text-gray-400">Fornecedores</div>
          <div className="mt-1 text-3xl font-semibold text-sky-600 dark:text-sky-400">
            {totals.suppliers}
          </div>
        </div>
        <div className="rounded-lg bg-white p-5 shadow dark:bg-gray-800">
          <div className="text-xs text-gray-500 dark:text-gray-400">Qtd. comprada</div>
          <div className="mt-1 text-3xl font-semibold text-orange-600 dark:text-orange-400">
            {formatQuantity(totals.quantity)}
          </div>
        </div>
        <div className="rounded-lg bg-white p-5 shadow dark:bg-gray-800">
          <div className="text-xs text-gray-500 dark:text-gray-400">Custo total</div>
          <div className="mt-1 text-2xl font-semibold text-green-600 dark:text-green-400 sm:text-3xl">
            {formatCurrency(totals.amount)}
          </div>
        </div>
      </div>

      <div className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-lg bg-white p-5 shadow dark:bg-gray-800">
          <Text size="sm" className="mb-1 font-medium text-gray-700 dark:text-gray-300">
            Maior custo por fornecedor
          </Text>
          <Text size="xs" color="muted" className="mb-3">
            Top 10 pelo valor total das NF-e confirmadas
          </Text>
          <div style={{ height: chartHeight(topSuppliersChart.length || 2) }}>
            {topSuppliersChart.length === 0 ? (
              <EmptyChart />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart layout="vertical" data={topSuppliersChart} margin={{ left: 8, right: 16 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 12 }} />
                  <YAxis type="category" dataKey="name" width={160} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(value) => formatCurrency(Number(value || 0))} />
                  <Bar dataKey="count" name="Custo" fill="#008B95" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
        <div className="rounded-lg bg-white p-5 shadow dark:bg-gray-800">
          <Text size="sm" className="mb-1 font-medium text-gray-700 dark:text-gray-300">
            Maior custo por item
          </Text>
          <Text size="xs" color="muted" className="mb-3">
            Top 10 pelo valor total comprado (NF confirmadas)
          </Text>
          <div style={{ height: chartHeight(topItemsChart.length || 2) }}>
            {topItemsChart.length === 0 ? (
              <EmptyChart />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart layout="vertical" data={topItemsChart} margin={{ left: 8, right: 16 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 12 }} />
                  <YAxis type="category" dataKey="name" width={160} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(value) => formatCurrency(Number(value || 0))} />
                  <Bar dataKey="count" name="Custo" fill="#22c55e" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      <div className="rounded-lg bg-white shadow dark:bg-gray-800">
        <div className="border-b border-gray-200 px-5 py-4 dark:border-gray-700">
          <Text size="sm" className="font-medium text-gray-700 dark:text-gray-300">
            Histórico de compras
          </Text>
          <Text size="xs" color="muted" className="mt-0.5">
            Itens de NF-e vinculados ao estoque, com custo e fornecedor. Cards e gráficos consideram
            só notas confirmadas; rascunhos aparecem na tabela.
          </Text>
        </div>
        {purchaseRows.length === 0 ? (
          <div className="px-5 py-10 text-center text-sm text-gray-400">
            Nenhuma compra de produto encontrada nas notas fiscais.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900/40">
                <tr>
                  <th className="px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Data
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    NF
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Item
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Fornecedor
                  </th>
                  <th className="px-5 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Qtd
                  </th>
                  <th className="px-5 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Custo unit.
                  </th>
                  <th className="px-5 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Custo total
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {purchaseRows.map((row) => (
                  <tr key={row.key} className="bg-white dark:bg-gray-800">
                    <td className="whitespace-nowrap px-5 py-3 text-sm text-gray-600 dark:text-gray-300">
                      {formatDateOnlyPtBr(row.issueDate)}
                    </td>
                    <td className="px-5 py-3 text-sm text-gray-900 dark:text-white">
                      {row.invoiceNumber}
                      <span className="ml-2 text-xs text-gray-400">
                        {row.invoiceStatus === 'confirmed' ? 'Confirmada' : 'Rascunho'}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-sm text-gray-900 dark:text-white">
                      {row.itemCode != null ? `${row.itemCode} — ` : ''}
                      {row.itemName}
                    </td>
                    <td className="px-5 py-3 text-sm text-gray-600 dark:text-gray-300">
                      {row.supplierName}
                    </td>
                    <td className="px-5 py-3 text-right text-sm tabular-nums text-gray-900 dark:text-white">
                      {formatQuantity(row.quantity)} {row.itemUnit}
                    </td>
                    <td className="px-5 py-3 text-right text-sm tabular-nums text-gray-900 dark:text-white">
                      {formatCurrency(row.unitValue)}
                    </td>
                    <td className="px-5 py-3 text-right text-sm tabular-nums font-medium text-gray-900 dark:text-white">
                      {formatCurrency(row.totalValue)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  )
}

const EstoqueDashboard: React.FC<Props> = (props) => {
  const [activeTab, setActiveTab] = useState<DashTab>('produtos')

  return (
    <div>
      <div className="mb-6 border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex gap-6" aria-label="Dashboard de estoque">
          <button
            type="button"
            className={tabButtonClass(activeTab === 'produtos')}
            onClick={() => setActiveTab('produtos')}
          >
            Produtos
          </button>
          <button
            type="button"
            className={tabButtonClass(activeTab === 'compras')}
            onClick={() => setActiveTab('compras')}
          >
            Compras
          </button>
        </nav>
      </div>

      {activeTab === 'produtos' ? <ProdutosPanel {...props} /> : <ComprasPanel />}
    </div>
  )
}

export default EstoqueDashboard
